/**
 * IntentPrefillBanner — Intent Prefill IP5.
 *
 * Compact Project-Intent suggestion surface for the NewReportWizard's
 * preferences step. Mirrors the Rerun wizard's IP2 affordance: per-field
 * SuggestionChips for workloadProfile / nuclearInterest / priority + an
 * Apply-all CTA, gated on undecided fields. Accept clicks update the
 * site's `projectIntent` so the seed flow that feeds the wizard
 * (`preferencesFromIntent(reportType, site.projectIntent)`)
 * inherits the fresh values immediately.
 *
 * Banner self-hides when there's nothing useful to surface — no
 * undecided fields OR every suggestion came from the fallback tier.
 * Pure presentational + one async write (updateSite); no Firestore
 * reads here.
 */
import React, { useMemo } from 'react';
import { Sparkles } from 'lucide-react';
import { workloadProfileLabel } from './WorkloadProfilePicker';
import { SuggestionChip } from './SuggestionChip';
import {
  suggestProjectIntent,
  hasNonFallbackSuggestions,
  type SuggestedProjectIntent,
} from '../../lib/suggestProjectIntent';
import type { SiteEntity } from '../../types/site';
import type { ProjectIntent, SiteRagReport, SiteDocument } from '../../types/siteRag';
import type { CompanyArchetype } from '../../types/company';

export interface IntentPrefillBannerProps {
  site: SiteEntity;
  /** Other reports on this site — used by the suggester's prior-report
   *  tier. Empty / undefined for fresh sites; banner still surfaces
   *  archetype-default suggestions. */
  recentReports?: SiteRagReport[];
  /** Site documents — used by the suggester's document-extracted tier. */
  documents?: SiteDocument[];
  /** Active company archetype (or per-site override). Drives the
   *  archetype-default tier. */
  archetype?: CompanyArchetype;
  /** Async write — updates the site's projectIntent. The banner
   *  stamps the accepted field + its source on the fieldSource map. */
  onApply: (next: ProjectIntent) => void | Promise<void>;
  /** Optional test id; defaults to "intent-prefill-banner". */
  testId?: string;
}

const NUCLEAR_LABEL: Record<'primary' | 'considering' | 'no', string> = {
  primary: 'Primary',
  considering: 'Considering',
  no: 'No',
};
const PRIORITY_LABEL: Record<'cost' | 'speed' | 'sustainability' | 'resilience', string> = {
  cost: 'Cost',
  speed: 'Speed',
  sustainability: 'Sustainability',
  resilience: 'Resilience',
};

const DEFAULT_INTENT: ProjectIntent = {
  workloadProfile: 'mixed',
  powerRequirement: 'undecided',
  coolingStrategy: 'undecided',
  nuclearInterest: 'undecided',
  timeline: 'undecided',
  priority: 'undecided',
};

export function IntentPrefillBanner({
  site,
  recentReports,
  documents,
  archetype,
  onApply,
  testId = 'intent-prefill-banner',
}: IntentPrefillBannerProps) {
  const current = site.projectIntent ?? DEFAULT_INTENT;

  const suggestion: SuggestedProjectIntent = useMemo(
    () => suggestProjectIntent({
      site,
      recentReports,
      documents: documents ?? site.documents ?? [],
      archetype: archetype ?? site.archetypeOverride ?? undefined,
    }),
    [site, recentReports, documents, archetype],
  );

  // `mixed` is the platform default sentinel — only treat it as undecided when
  // the user has not authored it (no fieldSource, or explicit fallback tier).
  const workloadProvenance = current.fieldSource?.workloadProfile;
  const workloadUndecided =
    !current.workloadProfile
    || (current.workloadProfile === 'mixed'
      && (!workloadProvenance || workloadProvenance === 'default' || workloadProvenance === 'fallback'));
  const nuclearUndecided = current.nuclearInterest === 'undecided';
  const priorityUndecided = current.priority === 'undecided';

  const undecidedCount =
    (workloadUndecided ? 1 : 0) + (nuclearUndecided ? 1 : 0) + (priorityUndecided ? 1 : 0);
  if (undecidedCount === 0) return null;
  if (!hasNonFallbackSuggestions(suggestion)) return null;

  const acceptOne = (field: 'workloadProfile' | 'nuclearInterest' | 'priority') => {
    const next: ProjectIntent = { ...current };
    const nextFieldSource = { ...(current.fieldSource ?? {}) };
    if (field === 'workloadProfile') {
      next.workloadProfile = suggestion.workloadProfile.value;
      nextFieldSource.workloadProfile = suggestion.workloadProfile.source;
    } else if (field === 'nuclearInterest') {
      next.nuclearInterest = suggestion.nuclearInterest.value;
      nextFieldSource.nuclearInterest = suggestion.nuclearInterest.source;
    } else {
      next.priority = suggestion.priority.value;
      nextFieldSource.priority = suggestion.priority.source;
    }
    next.fieldSource = nextFieldSource;
    void onApply(next);
  };
  const acceptAll = () => {
    const next: ProjectIntent = { ...current };
    const nextFieldSource = { ...(current.fieldSource ?? {}) };
    if (workloadUndecided) {
      next.workloadProfile = suggestion.workloadProfile.value;
      nextFieldSource.workloadProfile = suggestion.workloadProfile.source;
    }
    if (nuclearUndecided) {
      next.nuclearInterest = suggestion.nuclearInterest.value;
      nextFieldSource.nuclearInterest = suggestion.nuclearInterest.source;
    }
    if (priorityUndecided) {
      next.priority = suggestion.priority.value;
      nextFieldSource.priority = suggestion.priority.source;
    }
    next.fieldSource = nextFieldSource;
    void onApply(next);
  };

  return (
    <div
      className="border border-primary/20 bg-primary/5 p-3 space-y-2"
      data-testid={testId}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Sparkles className="w-3 h-3 text-primary" />
          <span className="type-body font-bold uppercase tracking-widest text-primary">
            Project intent suggestions
          </span>
        </div>
        <button
          type="button"
          onClick={acceptAll}
          className="type-body uppercase tracking-widest font-bold text-primary hover:text-primary/80 underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
          data-testid="intent-prefill-banner-apply-all"
        >
          Apply all →
        </button>
      </div>
      <div className="text-[10px] text-text-secondary leading-relaxed">
        Based on your site, archetype, and uploaded docs — pre-fill any of the {undecidedCount} undecided field{undecidedCount === 1 ? '' : 's'}.
      </div>
      <div className="flex flex-wrap gap-2 pt-1">
        {workloadUndecided && suggestion.workloadProfile.source !== 'fallback' && (
          <div className="flex items-center gap-2">
            <span className="type-meta uppercase tracking-widest text-text-muted">Workload</span>
            <SuggestionChip
              value={suggestion.workloadProfile.value}
              valueLabel={workloadProfileLabel(suggestion.workloadProfile.value)}
              rationale={suggestion.workloadProfile.rationale}
              confidence={suggestion.workloadProfile.confidence}
              source={suggestion.workloadProfile.source}
              onAccept={() => acceptOne('workloadProfile')}
              testId="intent-prefill-banner-workload"
            />
          </div>
        )}
        {nuclearUndecided && suggestion.nuclearInterest.source !== 'fallback' && (
          <div className="flex items-center gap-2">
            <span className="type-meta uppercase tracking-widest text-text-muted">Nuclear</span>
            <SuggestionChip
              value={suggestion.nuclearInterest.value}
              valueLabel={NUCLEAR_LABEL[suggestion.nuclearInterest.value]}
              rationale={suggestion.nuclearInterest.rationale}
              confidence={suggestion.nuclearInterest.confidence}
              source={suggestion.nuclearInterest.source}
              onAccept={() => acceptOne('nuclearInterest')}
              testId="intent-prefill-banner-nuclear"
            />
          </div>
        )}
        {priorityUndecided && suggestion.priority.source !== 'fallback' && (
          <div className="flex items-center gap-2">
            <span className="type-meta uppercase tracking-widest text-text-muted">Priority</span>
            <SuggestionChip
              value={suggestion.priority.value}
              valueLabel={PRIORITY_LABEL[suggestion.priority.value]}
              rationale={suggestion.priority.rationale}
              confidence={suggestion.priority.confidence}
              source={suggestion.priority.source}
              onAccept={() => acceptOne('priority')}
              testId="intent-prefill-banner-priority"
            />
          </div>
        )}
      </div>
    </div>
  );
}
